import { getOneByRemote, updateOne } from 'serviceWorker/db/documentations/documentations';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';

export interface CurrentVersionChange {
  currentVersionId: string;
}

/** How a caller sends a documentation's changes to the API (the queue's uploader, or a direct request). */
export type PutDocumentation = (changes: CurrentVersionChange) => Promise<unknown>;

/**
 * Runs the write on the device. An operation that may outlive its project
 * passes its scope's `write`, so the write is skipped once the data is outdated.
 */
export type WriteOnDevice = (write: () => Promise<void>) => Promise<unknown>;

const writeAtOnce: WriteOnDevice = (write) => write();

/**
 * Makes a version the documentation's current one, on the backend and on the
 * device. Since PT-7117 an upload or an export does not do it by itself: a
 * documentation without a current version has nothing to show or to map.
 */
export async function setCurrentVersion(
  putDocumentation: PutDocumentation,
  documentationId: string,
  versionId: string,
  writeOnDevice: WriteOnDevice = writeAtOnce
): Promise<void> {
  await putDocumentation({ currentVersionId: versionId });
  await writeOnDevice(async () => {
    const stored = await getOneByRemote(documentationId);
    if (stored) {
      await updateOne(stored.localId, { currentVersionId: versionId });
    }
  });
}

/** What created the new version of a documentation, for the log of a failure. */
export enum NewVersionSource {
  upload = 'upload',
  export = 'export',
}

export interface NewVersion {
  documentationId: string;
  versionId: string;
  source: NewVersionSource;
  /** The project the documentation belongs to, for the log. */
  projectId?: string;
}

/**
 * Makes a new version current: the first one of a new documentation, or one added to
 * an existing documentation (an upload or an export). Its file is stored already: a
 * failure is logged, not thrown, and the documentation keeps the current version it had.
 * Resolves whether the version was made current.
 */
export async function makeNewVersionCurrent(
  putDocumentation: PutDocumentation,
  { documentationId, versionId, source, projectId }: NewVersion,
  writeOnDevice: WriteOnDevice = writeAtOnce
): Promise<boolean> {
  try {
    await setCurrentVersion(putDocumentation, documentationId, versionId, writeOnDevice);
    return true;
  } catch (error) {
    swLog('New documentation version could not be made current', LogLevel.ERROR, error, {
      entityName: 'documentations',
      projectId,
      query: { documentationId, versionId, source },
    });
    return false;
  }
}
